import React from 'react';
import { Link } from 'react-router-dom';

export function renderFooterContent(pathname) {
    switch (pathname) {
        case '/documentation':          return <Documentation />;
        case '/support':                return <Support />;
        case '/privacy-terms':          return <PrivacyTerms />;
        case '/sitemap':                return <Sitemap />;
        case '/customers':              return <Customers />;
        case '/about-us':               return <AboutUs />;
        case '/business-partnership':   return <BusinessPartnership />;
        case '/blogs':                  return <Blogs />;
        case '/contact-us':             return <ContactUs />;
        case '/privacy-policy':         return <PrivacyPolicy />;
        case '/terms-and-conditions':   return <TermsAndConditions />;
        case '/return-policy':          return <ReturnPolicy />;
        case '/refund-policy':          return <RefundPolicy />;
        case '/ip-policy':              return <IPPolicy />;
        default:                        return null;
    }
}

function Documentation() {
    return (
        <>
            <p>
                Welcome to the Coliza help centre. This guide explains how to browse, order, and manage
                your medicines and health products on our online pharmacy platform.
            </p>

            <h2>Getting Started</h2>
            <p>
                Create a free account using your email address. After registration, verify your email
                through the link we send you. Once verified, you can log in, save addresses, and track orders.
            </p>

            <h2>Browsing Products</h2>
            <ul>
                <li>Use the search bar to find medicines and health products by name.</li>
                <li>Browse categories from the home page or visit <Link to="/categories">All Categories</Link>.</li>
                <li>View product details, stock availability, shipping options, and customer reviews on each product page.</li>
            </ul>

            <h2>Placing an Order</h2>
            <ol>
                <li>Add items to your cart or use Buy Now for a single product.</li>
                <li>Proceed to checkout and enter or select a delivery address.</li>
                <li>Enter your 6-digit pincode to confirm delivery availability in your area.</li>
                <li>Choose a payment method — online payment or Cash on Delivery (where available).</li>
                <li>Review your order summary. Prices include applicable GST (18%).</li>
                <li>Confirm the order. You will receive a confirmation once it is placed successfully.</li>
            </ol>

            <h2>Managing Your Account</h2>
            <p>
                From your account area you can update personal details, change your password, view order
                history, manage saved addresses, and maintain your wishlist.
            </p>

            <h2>Delivery &amp; Payments</h2>
            <p>
                Delivery timelines depend on your location and product availability. Some products qualify
                for free shipping. Payment options include secure online payment and COD on eligible orders.
                Shipping and COD charges, when applicable, are shown at checkout before you confirm.
            </p>

            <h2>Need More Help?</h2>
            <p>
                Visit our <Link to="/support">Support</Link> page or <Link to="/contact-us">Contact Us</Link> if
                you have questions not covered here.
            </p>
        </>
    );
}

function Support() {
    return (
        <>
            <p>
                Our support team is here to help you with orders, deliveries, payments, returns, and
                general questions about shopping on Coliza.
            </p>

            <h2>Common Questions</h2>

            <h3>How do I track my order?</h3>
            <p>
                Log in to your account and open <strong>My Orders</strong> to view order status and details
                for every purchase you have made.
            </p>

            <h3>How do I check if you deliver to my area?</h3>
            <p>
                On any product page or at checkout, enter your 6-digit pincode. The system will confirm
                whether delivery is available at your location.
            </p>

            <h3>What payment methods do you accept?</h3>
            <p>
                We accept secure online payments as well as Cash on Delivery (COD) on eligible products.
                Available options are displayed at checkout.
            </p>

            <h3>Can I cancel or modify an order?</h3>
            <p>
                If your order has not yet been dispatched, contact us as soon as possible. Once shipped,
                cancellation may not be possible — please refer to our <Link to="/return-policy">Return Policy</Link>.
            </p>

            <h3>How do returns work?</h3>
            <p>
                Eligible products may be returned within 14 days of delivery. See our{' '}
                <Link to="/return-policy">Return Policy</Link> and <Link to="/refund-policy">Refund Policy</Link> for
                full details.
            </p>

            <div className="fp-card">
                <h2 style={{ marginTop: 0 }}>Still need help?</h2>
                <p style={{ marginBottom: 0 }}>
                    Email us at <a href="mailto:support@coliza.in">support@coliza.in</a> or visit our{' '}
                    <Link to="/contact-us">Contact Us</Link> page. We aim to respond within one business day.
                </p>
            </div>
        </>
    );
}

function PrivacyTerms() {
    return (
        <>
            <p>
                This page provides a summary of how Coliza handles your information and the key terms
                that apply when you use our website. For complete details, please read our full{' '}
                <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
                <Link to="/terms-and-conditions">Terms and Conditions</Link>.
            </p>

            <h2>Your Privacy</h2>
            <ul>
                <li>We collect only the information needed to process orders and provide customer support.</li>
                <li>Personal data such as name, email, phone number, and delivery address is stored securely.</li>
                <li>We do not sell your personal information to third parties.</li>
                <li>Payment processing is handled through secure, trusted payment partners.</li>
                <li>You may request correction or deletion of your account data by contacting us.</li>
            </ul>

            <h2>Key Terms of Use</h2>
            <ul>
                <li>Coliza is an online pharmacy platform for purchasing medicines and health products.</li>
                <li>Product information on the site is for general reference. Always follow your healthcare provider&apos;s advice.</li>
                <li>Prices shown include GST where applicable and are confirmed at checkout.</li>
                <li>Orders are subject to stock availability and delivery coverage in your pincode.</li>
                <li>Abuse of the platform, fraudulent orders, or violation of applicable laws may result in account suspension.</li>
            </ul>

            <p>
                By using Coliza, you agree to these principles and our full legal policies linked above.
            </p>
        </>
    );
}

function Sitemap() {
    return (
        <>
            <p>
                Find all main sections of the Coliza website below. Use this page to navigate quickly
                to any area of our online pharmacy.
            </p>

            <div className="fp-sitemap-grid">
                <div className="fp-sitemap-col">
                    <h3>Shop</h3>
                    <ul>
                        <li><Link to="/">Home</Link></li>
                        <li><Link to="/categories">All Categories</Link></li>
                        <li><Link to="/category">All Medicines</Link></li>
                        <li><Link to="/cart">Shopping Cart</Link></li>
                    </ul>
                </div>

                <div className="fp-sitemap-col">
                    <h3>Account</h3>
                    <ul>
                        <li><Link to="/account/accountDetails">My Account</Link></li>
                        <li><Link to="/myOrders">My Orders</Link></li>
                        <li><Link to="/account/wishlist">Wishlist</Link></li>
                        <li><Link to="/account/addresses">Saved Addresses</Link></li>
                        <li><Link to="/changepassword">Change Password</Link></li>
                    </ul>
                </div>

                <div className="fp-sitemap-col">
                    <h3>Help</h3>
                    <ul>
                        <li><Link to="/about-us">About Us</Link></li>
                        <li><Link to="/contact-us">Contact Us</Link></li>
                        <li><Link to="/support">Support</Link></li>
                        <li><Link to="/documentation">Documentation</Link></li>
                        <li><Link to="/blogs">Blogs</Link></li>
                        <li><Link to="/customers">Customers</Link></li>
                    </ul>
                </div>

                <div className="fp-sitemap-col">
                    <h3>Policies</h3>
                    <ul>
                        <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                        <li><Link to="/terms-and-conditions">Terms and Conditions</Link></li>
                        <li><Link to="/return-policy">Return Policy</Link></li>
                        <li><Link to="/refund-policy">Refund Policy</Link></li>
                        <li><Link to="/ip-policy">IP Policy</Link></li>
                        <li><Link to="/privacy-terms">Privacy &amp; Terms</Link></li>
                    </ul>
                </div>

                <div className="fp-sitemap-col">
                    <h3>Business</h3>
                    <ul>
                        <li><Link to="/business-partnership">Business Partnership</Link></li>
                    </ul>
                </div>
            </div>
        </>
    );
}

function Customers() {
    return (
        <>
            <p>
                Whether you are ordering for yourself or your family, Coliza is built to make buying
                medicines and health essentials simple, safe, and reliable.
            </p>

            <h2>Why Shop With Coliza?</h2>
            <ul>
                <li><strong>Wide selection</strong> — Browse medicines and health products across multiple categories.</li>
                <li><strong>Convenient delivery</strong> — Order from home and have products delivered to your doorstep.</li>
                <li><strong>Secure checkout</strong> — Pay online through trusted payment gateways or choose COD where available.</li>
                <li><strong>Order tracking</strong> — View your complete order history anytime from your account.</li>
                <li><strong>Saved addresses</strong> — Store delivery addresses for faster repeat orders.</li>
                <li><strong>Wishlist</strong> — Save products you want to purchase later.</li>
            </ul>

            <h2>Your Shopping Journey</h2>
            <ol>
                <li><strong>Discover</strong> — Search or browse categories to find what you need.</li>
                <li><strong>Review</strong> — Check product details, stock, delivery options, and reviews.</li>
                <li><strong>Order</strong> — Add to cart, confirm your address and pincode, and pay securely.</li>
                <li><strong>Receive</strong> — Get your order delivered and track it from your account.</li>
                <li><strong>Support</strong> — Reach out anytime through our <Link to="/support">Support</Link> page.</li>
            </ol>

            <h2>Responsible Use</h2>
            <p>
                Coliza sells health-related products. Always read product labels and consult a qualified
                healthcare professional before starting or changing any medication. Do not use prescription
                medicines without a valid prescription where required by law.
            </p>

            <div className="fp-card">
                <p style={{ margin: 0 }}>
                    New to Coliza? <Link to="/documentation">Read our getting-started guide</Link> or{' '}
                    <Link to="/contact-us">get in touch</Link> — we are happy to help.
                </p>
            </div>
        </>
    );
}

function AboutUs() {
    return (
        <>
            <p>
                Coliza is a trusted online pharmacy dedicated to making medicines and health products
                accessible to everyone. We deliver quality healthcare essentials right to your door —
                safely, conveniently, and at fair prices.
            </p>

            <h2>Our Mission</h2>
            <p>
                We believe that access to reliable medicines and wellness products should not be difficult.
                Our mission is to connect customers with the health products they need through a simple
                digital shopping experience backed by dependable delivery and responsive support.
            </p>

            <h2>What We Offer</h2>
            <ul>
                <li>A curated catalogue of medicines and health products</li>
                <li>Pincode-based delivery across supported service areas</li>
                <li>Secure online payments and Cash on Delivery options</li>
                <li>Transparent pricing with GST included in displayed amounts</li>
                <li>Customer accounts for order history, addresses, and wishlists</li>
            </ul>

            <h2>Our Commitment</h2>
            <p>
                We are committed to product quality, data privacy, and customer satisfaction. Every order
                is processed with care, and our team works to resolve issues promptly. We continuously
                improve our platform to serve you better.
            </p>

            <h2>Shop With Confidence</h2>
            <p>
                From browsing to delivery, Coliza is designed to give you a smooth and trustworthy
                pharmacy shopping experience. Explore our catalogue on the <Link to="/">home page</Link> or
                contact us if you have any questions.
            </p>
        </>
    );
}

function BusinessPartnership() {
    return (
        <>
            <p>
                Coliza welcomes partnerships with pharmacies, distributors, healthcare brands, and logistics
                providers who share our commitment to accessible, quality healthcare products.
            </p>

            <h2>Partnership Opportunities</h2>
            <ul>
                <li><strong>Product suppliers</strong> — List your medicines and health products on our platform.</li>
                <li><strong>Pharmacy networks</strong> — Collaborate on fulfilment and last-mile delivery.</li>
                <li><strong>Healthcare brands</strong> — Promote wellness and OTC products to our customer base.</li>
                <li><strong>Logistics partners</strong> — Help us expand delivery coverage to new regions.</li>
            </ul>

            <h2>Why Partner With Coliza?</h2>
            <ul>
                <li>Reach customers actively searching for medicines and health products online</li>
                <li>Benefit from an established e-commerce platform with secure payments</li>
                <li>Grow alongside a pharmacy-focused brand built for trust and convenience</li>
                <li>Work with a team dedicated to compliance and customer satisfaction</li>
            </ul>

            <h2>How to Get Started</h2>
            <p>
                If you are interested in a business partnership, we would like to hear from you. Share
                a brief overview of your organisation, the type of partnership you are proposing, and
                your contact details.
            </p>

            <div className="fp-card">
                <p style={{ margin: 0 }}>
                    Email our partnerships team at{' '}
                    <a href="mailto:partnerships@coliza.in">partnerships@coliza.in</a> or use our{' '}
                    <Link to="/contact-us">Contact Us</Link> form. We review all enquiries and respond
                    within a few business days.
                </p>
            </div>
        </>
    );
}

function Blogs() {
    const articles = [
        {
            title: '5 Tips for Storing Medicines Safely at Home',
            date: 'March 2026',
            body: [
                'Proper storage keeps medicines effective and safe. Keep products in their original packaging so you always have dosage instructions and expiry dates visible.',
                'Store most medicines in a cool, dry place away from direct sunlight. Avoid bathroom cabinets — heat and humidity can degrade active ingredients.',
                'Keep medicines out of reach of children and pets. Use a locked cabinet if needed.',
                'Check expiry dates regularly and dispose of expired products safely through a pharmacy take-back programme where available.',
                'Never share prescription medicines with others, even if symptoms seem similar.',
            ],
        },
        {
            title: 'How to Build a Basic Home Health Kit',
            date: 'February 2026',
            body: [
                'A well-stocked home health kit helps you handle minor ailments quickly. Start with essentials: adhesive bandages, antiseptic solution, sterile gauze, medical tape, and a digital thermometer.',
                'Include over-the-counter items your family commonly uses — such as pain relievers, antihistamines, or oral rehydration salts — but always read labels and follow recommended dosages.',
                'Add personal items like prescribed medications, allergy information, and emergency contact numbers.',
                'Review your kit every few months. Replace used items and check expiry dates on all products.',
            ],
        },
        {
            title: 'Understanding Online Pharmacy Orders',
            date: 'January 2026',
            body: [
                'Ordering medicines online saves time, but it helps to know what to expect. After placing an order, you receive a confirmation with your order details.',
                'Delivery times depend on your location and product availability. Entering your pincode before checkout confirms whether we can deliver to your address.',
                'Keep your account information up to date — especially your phone number and delivery address — so our team can reach you if needed.',
                'If something arrives damaged or incorrect, contact support promptly. Most issues are resolved quickly when reported early.',
            ],
        },
        {
            title: 'When to Consult a Doctor Before Self-Medicating',
            date: 'December 2025',
            body: [
                'Over-the-counter products can help with mild symptoms, but they are not a substitute for professional medical advice in every situation.',
                'See a doctor if symptoms are severe, persist beyond a few days, or worsen despite treatment. This includes high fever, difficulty breathing, chest pain, or unexplained weight loss.',
                'Always inform your doctor about other medicines you take to avoid harmful interactions.',
                'Coliza provides access to health products — your healthcare provider remains your best guide for diagnosis and treatment decisions.',
            ],
        },
    ];

    return (
        <>
            <p>
                Health tips, wellness guidance, and practical advice for making the most of your online
                pharmacy experience. All articles are written for general informational purposes.
            </p>

            {articles.map((article) => (
                <article key={article.title} className="fp-article">
                    <h2 style={{ marginTop: 0 }}>{article.title}</h2>
                    <p className="fp-article-meta">{article.date}</p>
                    {article.body.map((para, i) => (
                        <p key={i}>{para}</p>
                    ))}
                </article>
            ))}

            <p>
                <em>
                    The information on this page is for general educational purposes only and does not
                    constitute medical advice. Consult a qualified healthcare professional for personal
                    health concerns.
                </em>
            </p>
        </>
    );
}

function ContactUs() {
    return (
        <>
            <p>
                We are here to help with orders, product enquiries, delivery questions, and feedback.
                Reach out through any of the channels below and our team will get back to you.
            </p>

            <div className="fp-card">
                <div className="fp-contact-row">
                    <div className="fp-contact-item">
                        <strong>Customer Support</strong>
                        <a href="mailto:support@coliza.in">support@coliza.in</a>
                    </div>
                    <div className="fp-contact-item">
                        <strong>Business Enquiries</strong>
                        <a href="mailto:partnerships@coliza.in">partnerships@coliza.in</a>
                    </div>
                    <div className="fp-contact-item">
                        <strong>Website</strong>
                        <a href="https://shop.coliza.in" target="_blank" rel="noopener noreferrer">
                            shop.coliza.in
                        </a>
                    </div>
                    <div className="fp-contact-item">
                        <strong>Support Hours</strong>
                        Monday – Saturday, 9:00 AM – 6:00 PM IST
                    </div>
                </div>
            </div>

            <h2>Before You Write</h2>
            <p>
                For faster assistance, please include your order number (if applicable), registered email
                address, and a brief description of your question. Order-related queries can often be
                resolved quickly through <Link to="/myOrders">My Orders</Link> in your account.
            </p>

            <h2>Common Topics</h2>
            <ul>
                <li>Order status and delivery updates</li>
                <li>Payment or refund enquiries</li>
                <li>Product availability and pincode coverage</li>
                <li>Returns and exchanges</li>
                <li>Account and login issues</li>
                <li>Business and partnership proposals</li>
            </ul>

            <p>
                You may also visit our <Link to="/support">Support</Link> page for answers to frequently
                asked questions.
            </p>
        </>
    );
}

function PrivacyPolicy() {
    return (
        <>
            <p><em>Last updated: June 2026</em></p>
            <p>
                Coliza (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates the online pharmacy platform at shop.coliza.in.
                This Privacy Policy explains how we collect, use, and protect your personal information.
            </p>

            <h2>Information We Collect</h2>
            <ul>
                <li><strong>Account information</strong> — name, email address, phone number, and password (stored securely).</li>
                <li><strong>Order information</strong> — delivery addresses, order history, and payment method selection.</li>
                <li><strong>Technical data</strong> — browser type, device information, and IP address for security and analytics.</li>
                <li><strong>Communications</strong> — messages you send to our support team.</li>
            </ul>

            <h2>How We Use Your Information</h2>
            <ul>
                <li>To process and deliver your orders</li>
                <li>To manage your account and provide customer support</li>
                <li>To send order confirmations and service-related notifications</li>
                <li>To improve our website, products, and services</li>
                <li>To prevent fraud and maintain platform security</li>
                <li>To comply with applicable legal obligations</li>
            </ul>

            <h2>Information Sharing</h2>
            <p>
                We do not sell your personal data. We may share limited information with trusted third
                parties only when necessary — such as payment processors for transactions and delivery
                partners for fulfilment — and only to the extent required for those services.
            </p>

            <h2>Data Security</h2>
            <p>
                We implement reasonable technical and organisational measures to protect your information.
                However, no method of transmission over the internet is completely secure, and we cannot
                guarantee absolute security.
            </p>

            <h2>Your Rights</h2>
            <p>
                You may request access to, correction of, or deletion of your personal data by contacting
                us at <a href="mailto:support@coliza.in">support@coliza.in</a>. You may also update
                account details directly from your profile.
            </p>

            <h2>Cookies</h2>
            <p>
                We use cookies and similar technologies to maintain your session and improve your browsing
                experience. You can control cookies through your browser settings.
            </p>

            <h2>Changes to This Policy</h2>
            <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page
                with an updated revision date.
            </p>

            <h2>Contact</h2>
            <p>
                For privacy-related questions, email <a href="mailto:support@coliza.in">support@coliza.in</a>.
            </p>
        </>
    );
}

function TermsAndConditions() {
    return (
        <>
            <p><em>Last updated: June 2026</em></p>
            <p>
                By accessing or using the Coliza website and services, you agree to these Terms and
                Conditions. Please read them carefully before placing an order.
            </p>

            <h2>1. About Coliza</h2>
            <p>
                Coliza is an online platform for purchasing medicines and health products. We facilitate
                orders between customers and fulfilment partners subject to product availability and
                delivery coverage.
            </p>

            <h2>2. Eligibility</h2>
            <p>
                You must be at least 18 years old to create an account and place orders. By using our
                services, you confirm that the information you provide is accurate and complete.
            </p>

            <h2>3. Products &amp; Pricing</h2>
            <ul>
                <li>Product descriptions and images are provided for reference. We strive for accuracy but do not warrant that descriptions are error-free.</li>
                <li>Prices are displayed in Indian Rupees (INR) and include GST (18%) where applicable.</li>
                <li>Prices and availability may change without notice. The price confirmed at checkout applies to your order.</li>
                <li>We reserve the right to limit quantities or refuse orders at our discretion.</li>
            </ul>

            <h2>4. Orders &amp; Payment</h2>
            <p>
                An order is confirmed only after successful payment (for online orders) or order
                acceptance (for COD). We may cancel orders due to stock unavailability, pricing errors,
                or suspected fraud, with a full refund where payment has been collected.
            </p>

            <h2>5. Delivery</h2>
            <p>
                Delivery is available only in pincode areas we service. Estimated delivery times are
                indicative and not guaranteed. Risk of loss passes to you upon delivery to the address
                provided.
            </p>

            <h2>6. Prescription Medicines</h2>
            <p>
                Where required by law, prescription medicines must be ordered with a valid prescription.
                You are responsible for complying with all applicable regulations regarding the purchase
                and use of medicines.
            </p>

            <h2>7. User Conduct</h2>
            <p>
                You agree not to misuse the platform, submit false information, interfere with site
                operations, or use the service for unlawful purposes.
            </p>

            <h2>8. Limitation of Liability</h2>
            <p>
                To the fullest extent permitted by law, Coliza is not liable for indirect, incidental,
                or consequential damages arising from use of the platform. Our total liability for any
                claim related to an order is limited to the amount paid for that order.
            </p>

            <h2>9. Governing Law</h2>
            <p>
                These terms are governed by the laws of India. Disputes shall be subject to the exclusive
                jurisdiction of courts in India.
            </p>

            <h2>10. Contact</h2>
            <p>
                Questions about these terms? Contact <a href="mailto:support@coliza.in">support@coliza.in</a>.
            </p>
        </>
    );
}

function ReturnPolicy() {
    return (
        <>
            <p><em>Last updated: June 2026</em></p>
            <p>
                We want you to be satisfied with your purchase. If you receive a damaged, defective, or
                incorrect product, our return policy below applies.
            </p>

            <h2>Return Window</h2>
            <p>
                Eligible products may be returned within <strong>14 days</strong> of delivery, provided
                they meet the conditions listed below. Products marked as non-returnable at the time of
                purchase are excluded.
            </p>

            <h2>Eligible Returns</h2>
            <ul>
                <li>Product received is damaged or defective</li>
                <li>Wrong product or quantity was delivered</li>
                <li>Product packaging is tampered or compromised on arrival</li>
                <li>Product is unused, in original packaging, and seals are intact (where applicable)</li>
            </ul>

            <h2>Non-Returnable Items</h2>
            <ul>
                <li>Products with broken seals or opened packaging (unless defective on arrival)</li>
                <li>Products past their expiry date at the time of delivery complaint</li>
                <li>Items explicitly marked as non-returnable on the product page</li>
                <li>Products damaged due to misuse or improper storage after delivery</li>
            </ul>

            <h2>How to Initiate a Return</h2>
            <ol>
                <li>Contact us at <a href="mailto:support@coliza.in">support@coliza.in</a> within 14 days of delivery.</li>
                <li>Provide your order number, product name, and reason for return.</li>
                <li>Include photos of the product and packaging if the item is damaged or incorrect.</li>
                <li>Our team will review your request and provide return instructions if approved.</li>
            </ol>

            <h2>Return Shipping</h2>
            <p>
                If the return is due to our error (wrong or defective item), we will arrange or reimburse
                return shipping. For other eligible returns, return shipping costs may apply as communicated
                during the return process.
            </p>

            <p>
                Refunds for approved returns are processed as described in our{' '}
                <Link to="/refund-policy">Refund Policy</Link>.
            </p>
        </>
    );
}

function RefundPolicy() {
    return (
        <>
            <p><em>Last updated: June 2026</em></p>
            <p>
                This policy explains how refunds are handled for cancelled orders, returned products,
                and payment issues on Coliza.
            </p>

            <h2>Order Cancellation Refunds</h2>
            <p>
                If your order is cancelled before dispatch — whether by you or by us due to stock
                unavailability — any payment collected will be refunded in full.
            </p>

            <h2>Return Refunds</h2>
            <p>
                Once a returned product is received and inspected, eligible refunds are initiated within
                5–7 business days. Refunds are credited to the original payment method used for the order.
                For Cash on Delivery orders, refunds are processed via bank transfer or another method
                we agree upon with you.
            </p>

            <h2>Partial Refunds</h2>
            <p>
                Partial refunds may apply if only part of an order is returned, if products are returned
                in a condition that reduces their value, or if shipping or COD charges are non-refundable
                as stated at checkout.
            </p>

            <h2>Failed or Duplicate Payments</h2>
            <p>
                If you were charged but the order was not confirmed, or if a duplicate charge occurred,
                contact us immediately. We will investigate and refund any erroneous charges after
                verification with our payment partner.
            </p>

            <h2>Refund Timeline</h2>
            <ul>
                <li>Refund initiated by Coliza: within 5–7 business days of approval</li>
                <li>Bank or payment provider processing: additional 5–10 business days depending on your bank</li>
            </ul>

            <h2>Contact</h2>
            <p>
                For refund enquiries, email <a href="mailto:support@coliza.in">support@coliza.in</a> with
                your order number and payment details.
            </p>
        </>
    );
}

function IPPolicy() {
    return (
        <>
            <p><em>Last updated: June 2026</em></p>
            <p>
                This Intellectual Property Policy describes how Coliza protects its own content and how
                we handle reports of infringement by third parties.
            </p>

            <h2>Our Intellectual Property</h2>
            <p>
                The Coliza name, logo, website design, text, graphics, software, and other content on
                shop.coliza.in are owned by or licensed to Coliza and protected by applicable copyright,
                trademark, and other intellectual property laws. You may not copy, reproduce, distribute,
                or create derivative works without our prior written consent.
            </p>

            <h2>Product Images &amp; Descriptions</h2>
            <p>
                Product images and descriptions may be supplied by manufacturers or distributors and
                remain the property of their respective owners. They are displayed on Coliza for
                informational and sales purposes with appropriate authorisation.
            </p>

            <h2>Permitted Use</h2>
            <p>
                You may view and print pages from our website for personal, non-commercial use only.
                Any other use — including scraping, automated data collection, or republication — is
                prohibited without permission.
            </p>

            <h2>Reporting Infringement</h2>
            <p>
                If you believe content on our platform infringes your intellectual property rights,
                please send a notice including:
            </p>
            <ul>
                <li>Your name and contact information</li>
                <li>Identification of the copyrighted work or trademark claimed to be infringed</li>
                <li>Identification of the material on our site that you claim is infringing</li>
                <li>A statement of good-faith belief that the use is not authorised</li>
                <li>A statement that the information in your notice is accurate</li>
            </ul>

            <h2>Contact</h2>
            <p>
                Send IP-related notices to <a href="mailto:legal@coliza.in">legal@coliza.in</a>. We
                review all valid reports and take appropriate action.
            </p>
        </>
    );
}
